"use client";

// ShareStudio — the share MOMENT, full-bleed.
//
// Sharing is the one action where something of yours leaves your hands, so it
// gets the whole screen rather than a card floating on a dimmed page. The screen
// splits in two and the two halves say different things:
//
//   LEFT — the PAGE ITSELF, live. Not a thumbnail, not a plate of ghost rules:
//     the real shared page, loaded in a frame at reading width and scaled to
//     fit, netti and all — and READABLE: it takes the pane's whole height and
//     scrolls inside its card, and a folder's children open in place exactly as
//     they will for the person you hand it to (law_share_folder). Every control
//     lands in it immediately — pick a ground and the page you are about to
//     hand over is already wearing it, type a title and it is already the
//     title. Nobody should have to open a link to find out what they made.
//
//   RIGHT — the DESK. The title, who the door opens for, the link itself, and
//     the code. The face's two editable parts (title · ground) are a DRAFT until
//     `post it live` — so you can try a title on without handing it out. Who the
//     door opens for is not a draft: it is a permission, and it lands the instant
//     you press it.
//
// THE COLOUR LIVES IN THE CORNER. There is one colour instrument in this
// product and it sits at the bottom-left of the screen (law_one_picker) — so it
// sits at the bottom-left HERE too, in the same place, wearing the same orb AT
// THE SAME SIZE: the resting orb is arcGeom's ORB at the dashboard dial's own
// scale, so the two can never drift apart. It used to float in the middle of
// the desk at nearly three times the size, which read as a second, unrelated
// dial that happened to be about colour.
//
// ONE WAY IN, WHATEVER IS IN VIEW. `share` opens this, always — a folder, a
// page, a skill, a page that is already public. Where there is no project to
// mint a token against (a public `/skills/<name>`, a published post) the studio
// opens READ-ONLY on the page's own url: the live preview, the code, the copy,
// and one line saying the page is public and the link is its interface. There
// is nothing to permit and nothing to let go of, so those controls are not
// there to be pressed.
//
// IT OPENS THE WAY EVERYTHING ELSE DOES: uncovered, not entered
// (law_always_there). The plate is the ground it will open in, pulled back
// toward the sharer's own, laid over the page they were just on — their netti
// still glowing through it, the same creature, not a second one. The desk is
// usable on the first frame; the page in the frame arrives behind it a beat
// later, and nothing waits on it.
//
// On a phone the two halves stack the other way round: the live page fills the
// screen and the desk RISES OVER IT as a sheet, with the dial still in the
// bottom-left corner above it.
//
// Rides the house language throughout: no boxes, the one pill, quiet links, and
// every colour traced to the ground or the ink ladder.

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import NettiQr, { nettiQrDownload } from "./NettiQr";
import { Field, PillButton } from "../ui";
import { ArcColorPicker, arcGeom } from "../os/ArcPicker";
import { hexToRgb, rgbToHex } from "../ColorInstrument";
import { applyGroundVars, inkRegime, type RGB } from "../../lib/theme";
import { PadlockCell, cellPath } from "../landing/CellIcons";
import ShareView, { type SharePayload } from "./ShareView";
import { seg, type OsNode } from "../../lib/os/tree";
import { lockScroll } from "../../lib/os/scrollLock";
import "./share.css";

/** a share, exactly as the API hands it over. */
export interface ShareRow {
  token: string;
  visibility: string;
  path: string;
  title?: string | null;
  ground?: string | null;
  space_id?: string | null;
  view_count: number;
  last_viewed_at?: string | null;
  created_at: string;
}

/** what the studio is opened ON — a page about to be shared, or a live share. */
export interface StudioTarget {
  /** the project to mint against. Absent only when `share` is already live. */
  projectId?: string | null;
  /** the page's path inside the project (law_share_scope). */
  path?: string;
  /** what the page is called when the sharer hasn't renamed the door. */
  name: string;
  /** the folder this page lives in — the face's quiet second line. */
  projectName?: string | null;
  /** the room this door was opened into, when it was opened into one. */
  spaceName?: string | null;
  /** an existing share, opened straight from the shared view. */
  share?: ShareRow | null;
  /** the sharer, for the face. Falls back to "you". */
  ownerName?: string | null;
  ownerAvatar?: string | null;
  /** a surface with NO project to mint against — a public skill page, a
   *  published post. The page's own url IS the door, so the studio opens
   *  read-only on it: the preview, the code, the copy, and one line. */
  publicUrl?: string | null;
  /** WHAT IS BEING SHARED, as the shell already holds it. The face renders the
   *  real reading surface from this, so the preview needs no token, no network
   *  and no second copy of the app (see the note on the face below). */
  node?: OsNode | null;
}

const HEX = /^#[0-9a-f]{6}$/i;

/** WHAT THE FACE RENDERS — the shared page, built from the node in hand.
 *
 *  The studio used to show the page in an IFRAME, which could not work here and
 *  was slow even where it could: the share link redirects from the dashboard's
 *  host to the apex one, so the frame was cross-origin and `x-frame-options:
 *  SAMEORIGIN` refused it outright ("this content is blocked"); a frame boots a
 *  whole second copy of the app, with a second organism, before anything can be
 *  seen; and none of it existed until a link had been minted, so you could never
 *  look at what you were about to hand over before handing it over.
 *
 *  So the face renders THE SAME COMPONENT the share page renders, from the node
 *  the shell already has. Instant, offline, honest — and the preview cannot
 *  drift from the real page, because it IS the real page's renderer. */
function payloadFrom(node: OsNode | null | undefined, target: StudioTarget, title: string | null): SharePayload | null {
  if (!node) return null;
  const when = node.lastActiveAt || node.createdAt || new Date().toISOString();
  const dims: SharePayload["dimensions"] = [];
  const take = (n: OsNode) => {
    const body = n.document || n.summary || "";
    if (n.icon === "page" || (!n.children?.length && body)) {
      dims.push({ name: n.editKey || seg(n.path) || n.name, content: body, lines: body.split("\n").length });
    }
    for (const c of n.children ?? []) take(c);
  };
  take(node);
  const isFolder = !!node.children?.length || node.icon === "folder";
  return {
    token: "preview",
    // the path decides which shape the reader opens in (law_share_scope), so it
    // is the real one — a folder opens as the space, a page as the page.
    path: target.path ?? node.path,
    visibility: "link",
    title,
    ground: null,
    space_name: target.spaceName ?? null,
    owner_key: null,
    owner_name: target.ownerName ?? null,
    owner_avatar: target.ownerAvatar ?? null,
    project: {
      id: target.projectId ?? "preview",
      slug: seg(node.path) || "preview",
      name: isFolder ? node.name : (target.projectName ?? node.name),
      type: "project",
      tagline: isFolder ? (node.summary ?? null) : null,
      updated_at: when,
    },
    dimensions: dims,
    nodes: [],
  };
}
/** the frame renders the page at a real reading width and is scaled to fit the
 *  pane's WIDTH — a preview at the pane's own size would be a phone-shaped
 *  desktop page. Its height is whatever the pane has, un-scaled, so the page
 *  scrolls inside its card the way the real one scrolls.
 *  THE WIDTH IS THE SHARER'S OWN SCREEN. A fixed 1280 was not the page they
 *  would see if they opened the link: every responsive rule lands somewhere
 *  else, and the reader column's own cap (`.share-head-row`, 820px) left
 *  "open yours" stranded mid-width in the preview while on a narrower screen it
 *  sits at the page's right edge. So the frame renders at the sharer's viewport
 *  width, held to a sane desk range, and the preview is faithful. */
const FRAME_MIN_W = 1024;
const FRAME_MAX_W = 1680;
const FRAME_NARROW_W = 400;
const frameWideW = () =>
  typeof window === "undefined" ? 1280 : Math.round(Math.min(FRAME_MAX_W, Math.max(FRAME_MIN_W, window.innerWidth)));
/** the dashboard's GroundDial breakpoint — the studio's orb follows the SAME
 *  one, so the two dials are the same size on the same screen. */
const DIAL_NARROW_MQ = "(max-width: 640px)";

/** the studio's own surface is the ground the link opens in, pulled back toward
 *  the sharer's own ground: enough of the mood to sit in it, never the same
 *  value as the page, so you can always see where the page ends. */
const PLATE_TINT = 0.42;
const mixRgb = (a: RGB, b: RGB, t: number): RGB => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
/** the ONE contrast rule, asked of any ground: does the ink ladder flip light?
 *  (lib/theme › inkRegime — never a second luminance threshold of our own.) */
const groundIsDeep = (rgb: RGB) => parseFloat(inkRegime(rgb).inkLight) > 50;

/** the live ground as a hex — the picker's seed when the share carries none. */
function groundHex(): string {
  if (typeof window === "undefined") return "#fffaf1";
  const v = getComputedStyle(document.documentElement).getPropertyValue("--bg").trim();
  if (HEX.test(v)) return v;
  const m = v.match(/(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
  if (!m) return "#fffaf1";
  return `#${[m[1], m[2], m[3]].map((c) => Number(c).toString(16).padStart(2, "0")).join("")}`;
}

/** the viewer's organism colour, read off the live vars, as concrete hsl —
 *  the downloaded QR must not depend on a CSS variable to keep its colours. */
function organismInks(): { hue: number; ink: string; core: string; soft: string } {
  const fallback = { hue: 210, ink: "hsl(210 35% 20%)", core: "hsl(185 60% 52%)", soft: "hsl(150 45% 66%)" };
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--organism-color").trim();
  const m = raw.match(/(\d+(?:\.\d+)?)[,\s]+(\d+(?:\.\d+)?)[,\s]+(\d+(?:\.\d+)?)/);
  if (!m) return fallback;
  const [r, g, b] = [Number(m[1]) / 255, Number(m[2]) / 255, Number(m[3]) / 255];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  if (d > 0) {
    if (max === r) h = 60 * (((g - b) / d) % 6);
    else if (max === g) h = 60 * ((b - r) / d + 2);
    else h = 60 * ((r - g) / d + 4);
  }
  h = (h + 360) % 360;
  const hs = Math.round(h);
  return {
    hue: hs,
    ink: `hsl(${hs} ${Math.round(Math.min(55, s * 100 * 0.7))}% 21%)`,
    core: `hsl(${hs} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`,
    soft: `hsl(${(hs + 40) % 360} ${Math.round(s * 80)}% ${Math.round(Math.min(74, l * 100 + 16))}%)`,
  };
}

/** WHO IT OPENS FOR, as two marks. Both wear the innernet cell (law_cell) — a
 *  bare circle would be a foreign shape here. The globe is the cell with the
 *  world's stripes running through it; the padlock is the one already drawn for
 *  the security surface (components/landing/CellIcons › PadlockCell), the cell
 *  as the lock's body. */
const GLOBE_CELL = cellPath(12, 12, 9);
function GlobeCell({ size = 20 }: { size?: number }) {
  const clip = useId().replace(/:/g, "");
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" aria-hidden>
      <defs>
        <clipPath id={`globe-${clip}`}>
          <path d={GLOBE_CELL} />
        </clipPath>
      </defs>
      <path d={GLOBE_CELL} stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <g clipPath={`url(#globe-${clip})`} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
        <path d="M2.6 9.4h18.8M2.6 14.6h18.8" />
        <ellipse cx="12" cy="12" rx="4.1" ry="9" />
      </g>
    </svg>
  );
}

/** the copy pill's badge — the link glyph at rest, a check the moment it lands. */
function CopyBadge({ done }: { done: boolean }) {
  return done ? (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12.5 10 17.5 19 7" />
    </svg>
  ) : (
    <svg viewBox="0 0 17 17" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <path d="M7 10a2.6 2.6 0 0 0 3.7 0l2.3-2.3a2.6 2.6 0 1 0-3.7-3.7l-1 1" />
      <path d="M10 7a2.6 2.6 0 0 0-3.7 0L4 9.3a2.6 2.6 0 1 0 3.7 3.7l1-1" />
    </svg>
  );
}

export default function ShareStudio({
  target,
  onClose,
  onChanged,
}: {
  target: StudioTarget;
  onClose: () => void;
  /** fired whenever a share is minted, edited, or let go — so a list refreshes. */
  onChanged?: (share: ShareRow | null) => void;
}) {
  // a page with no project behind it is already public: its own url IS the
  // door, there is nothing to mint, and nothing here can be permitted or let
  // go of. Everything that only makes sense for a minted door reads `live`.
  const publicUrl = target.publicUrl?.trim() || null;
  const live = !publicUrl;

  const [share, setShare] = useState<ShareRow | null>(target.share ?? null);
  const [url, setUrl] = useState<string | null>(
    target.share ? `${typeof location !== "undefined" ? location.origin : ""}/share/${target.share.token}` : publicUrl,
  );
  const [busy, setBusy] = useState(!target.share && live);
  const [err, setErr] = useState<string | null>(null);
  const [gone, setGone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  // has this door ever been posted? A share that arrives with a title or a
  // ground of its own was posted before this studio opened.
  const [wasPosted, setWasPosted] = useState(
    !!(target.share?.title || target.share?.ground),
  );
  const [confirmClose, setConfirmClose] = useState(false);
  const qrWrap = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const veilRef = useRef<HTMLDivElement>(null);
  const inks = useMemo(() => organismInks(), []);
  const seedGround = useMemo(() => groundHex(), []);
  const [narrow, setNarrow] = useState(false);
  const [dialNarrow, setDialNarrow] = useState(false);
  const [wideW, setWideW] = useState(frameWideW);
  const [frameFit, setFrameFit] = useState({ scale: 0.4, h: 600 });
  // THE SHEET PAINTS FIRST. The desk is usable on the first frame; the plate is
  // armed one frame later so the studio's own entrance is never competing with
  // laying out a whole reading surface beside it.
  const [frameArmed, setFrameArmed] = useState(false);
  useEffect(() => {
    // a beat later, never in the same commit as the studio's entrance. A TIMER,
    // not requestAnimationFrame: rAF never fires in a background tab, and a
    // studio opened there would sit on "nothing to show yet" until it was
    // looked at.
    const id = setTimeout(() => setFrameArmed(true), 0);
    return () => clearTimeout(id);
  }, []);

  // ── the draft face ───────────────────────────────────────────────────────
  // `title` and `ground` are what the plate is wearing. They are not on the
  // link until `post it live`, so a sharer can try a name on without handing
  // one out. `committed` is what the link actually carries.
  //
  // THE FIELD ARRIVES FULL. It holds the page's own name, so the sharer edits a
  // real title instead of typing one from nothing against a ghost placeholder.
  // That must not read as an unposted change: a value equal to the page's own
  // name is exactly what the link already says, so `postTitle` maps the field
  // back to what would actually be written (null = the page's own name) and
  // `dirty` compares THAT, never the raw text.
  const [title, setTitle] = useState(target.share?.title ?? target.name);
  const [ground, setGround] = useState<string | null>(target.share?.ground ?? null);
  const [committed, setCommitted] = useState<{ title: string; ground: string | null }>({
    title: target.share?.title ?? "",
    ground: target.share?.ground ?? null,
  });
  const asWritten = useCallback(
    (t: string) => {
      const v = t.trim();
      return !v || v === target.name.trim() ? null : v;
    },
    [target.name],
  );
  const postTitle = asWritten(title);
  const dirty = live && (postTitle !== asWritten(committed.title) || (ground ?? null) !== (committed.ground ?? null));

  const frameW = narrow ? FRAME_NARROW_W : wideW;
  const plateGround = ground ?? seedGround;
  // the page in the plate, rebuilt whenever the draft title changes — the title
  // is a prop of the real renderer now, so it needs no reaching into a document.
  const previewPage = useMemo(() => payloadFrom(target.node, target, postTitle), [target, postTitle]);
  // THE GROUND LANDS ON THE PLATE, NOT ON :root. One ground writer (lib/theme),
  // pointed at the preview's own box: the page inside flips its ink regime and
  // re-mints its gradient exactly as it will when someone opens the link, while
  // the studio around it keeps its own.
  const plateRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = plateRef.current;
    if (!el) return;
    applyGroundVars(hexToRgb(HEX.test(plateGround) ? plateGround : "#fffaf1"), el);
  }, [plateGround, previewPage]);
  // THE DIAL'S SIZE IS THE DASHBOARD DIAL'S SIZE. GroundDial rests its orb at
  // arcGeom(scale, "bl").ORB with scale 1 (0.7 under 640px); the collapsible
  // picker sizes its orb as (38·scale + 16)·orbScale, so orbScale is whatever
  // makes those two numbers meet — to the pixel, on every screen.
  const dialScale = dialNarrow ? 0.7 : 1;
  const dialOrb = arcGeom(dialScale, "bl").ORB;
  const dialOrbScale = dialOrb / (38 * dialScale + 16);

  const owner = target.ownerName?.trim() || "you";
  const openToAll = (share?.visibility ?? "link") === "link";
  const isRoom = !!(share?.space_id || target.spaceName);
  // a page inside a project's skills folder is a SKILL: opening it to anyone
  // with the link also lists it on the community skills page (law_skill_class —
  // making the door public IS publishing; there is no second switch).
  const isSkill = /\/skills\/[^/]+$/.test(share?.path ?? target.path ?? "");
  const audience = isRoom
    ? `for ${target.spaceName ?? "the room"}`
    : openToAll
      ? "anyone with the link"
      : "only you";
  const faceTitle = title.trim() || target.name;
  // THE CODE SITS IN THE PAGE'S MOOD. Its paper is the ground the link opens
  // in, so the code belongs to the thing it opens instead of floating on a
  // white chip — and the modules then follow the SAME contrast rule every other
  // mark on that ground follows (lib/theme › inkRegime), or a deep ground would
  // simply swallow them and the camera with them. The cell at its heart stays
  // white on every ground, the way THE button's disc does (law_buttons).
  const qrPaper = HEX.test(plateGround) ? plateGround : "#fffaf1";
  const qrDeep = groundIsDeep(hexToRgb(qrPaper));
  const qrInk = qrDeep ? `hsl(${inks.hue} 22% 94%)` : inks.ink;

  // ── arriving ─────────────────────────────────────────────────────────────
  // The studio arrives holding a live link: the one already minted for this
  // door, or a new one. Opened on an existing share it already has everything.
  useEffect(() => {
    if (target.share || publicUrl) return; // a public page already IS the door
    const projectId = target.projectId;
    const path = target.path ?? "/";
    if (!projectId) {
      setErr("there's no folder here to open a door into.");
      setBusy(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const land = (row: ShareRow, u: string) => {
        if (cancelled) return;
        setShare(row);
        setUrl(u);
        setTitle(row.title ?? target.name);
        setGround(row.ground ?? null);
        setCommitted({ title: row.title ?? "", ground: row.ground ?? null });
        setBusy(false);
        onChanged?.(row);
      };
      try {
        const res = await fetch(`/api/share?projectId=${projectId}`, { cache: "no-store" });
        const json = (await res.json()) as { shares?: ShareRow[] };
        const own = (json.shares ?? []).find((s) => s.path === path && !s.space_id);
        if (own) return land(own, `${location.origin}/share/${own.token}`);
      } catch {
        /* fall through to mint */
      }
      try {
        const res = await fetch("/api/share", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId, path, visibility: "link" }),
        });
        const json = (await res.json()) as { ok?: boolean; url?: string; share?: ShareRow; error?: string };
        if (json.ok && json.share && json.url) return land(json.share, json.url);
        if (!cancelled) {
          setErr(json.error ?? "sharing isn't reachable right now.");
          setBusy(false);
        }
      } catch {
        if (!cancelled) {
          setErr("sharing isn't reachable right now.");
          setBusy(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.projectId, target.path, publicUrl]);

  // ── leaving ──────────────────────────────────────────────────────────────
  // Escape asks about unposted changes, then closes. (The dial folds itself on
  // Escape first — it owns that key while its arcs are out.) Once the link has
  // been let go there is nothing left to post, so close simply closes.
  const tryClose = useCallback(() => {
    if (dirty && !gone) return setConfirmClose(true);
    onClose();
  }, [dirty, gone, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        tryClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tryClose]);

  // the page holds still while the desk is up. Its own effect, held for the
  // desk's whole life: `tryClose` changes with every edit, and the lock must
  // not let go and take hold again each time. The counted lock also means a
  // sheet under the desk closing first never hands the page back early.
  useEffect(() => lockScroll("share-studio"), []);

  // ── the live page in the frame ───────────────────────────────────────────
  // A phone shows a phone-shaped page; a desk shows a desk-shaped one. Both are
  // rendered at real reading size and scaled to fit their pane, so what you are
  // looking at is the page, not an approximation of it.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 899px), (max-height: 620px)");
    const dq = window.matchMedia(DIAL_NARROW_MQ);
    const on = () => {
      setNarrow(mq.matches);
      setDialNarrow(dq.matches);
      setWideW(frameWideW());
    };
    on();
    mq.addEventListener("change", on);
    dq.addEventListener("change", on);
    window.addEventListener("resize", on);
    return () => {
      mq.removeEventListener("change", on);
      dq.removeEventListener("change", on);
      window.removeEventListener("resize", on);
    };
  }, []);

  // one frame for the studio, then the page. rAF twice: the first lands in the
  // frame we are already painting, the second after it has been shown.
  useEffect(() => {
    let a = 0;
    const b = requestAnimationFrame(() => {
      a = requestAnimationFrame(() => setFrameArmed(true));
    });
    return () => {
      cancelAnimationFrame(b);
      cancelAnimationFrame(a);
    };
  }, []);

  // ── the plate the studio itself sits on ──────────────────────────────────
  // The ground the link opens in, pulled back toward the sharer's own, written
  // by THE ground writer (lib/theme › applyGroundVars) into the veil instead of
  // into :root — so the studio's whole surface, its gradient AND its ink regime
  // follow the pick exactly the way the real page does. `.ss-veil` re-declares
  // the ink ladder from these two numbers (share.css), which is what makes
  // white-on-deep and dark-on-pale both hold for every label on the plate.
  useEffect(() => {
    const el = veilRef.current;
    if (!el) return;
    const base = hexToRgb(HEX.test(seedGround) ? seedGround : "#fffaf1");
    const pick = hexToRgb(HEX.test(plateGround) ? plateGround : "#fffaf1");
    applyGroundVars(mixRgb(base, pick, PLATE_TINT), el);
  }, [seedGround, plateGround]);

  // fit to WIDTH, and give the frame the pane's whole height at that scale —
  // the card is the pane, and the page scrolls inside it.
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const fit = () => {
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const scale = r.width / frameW;
      setFrameFit({ scale, h: Math.ceil(r.height / scale) });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frameW]);

  // (the ground and the draft title are PROPS of the rendered page now, so the
  // three effects that used to reach into a frame's document — painting its
  // root, rewriting its headline, intercepting its links — are gone with it.)

  // the "posted" beat fades on its own — it's a beat, not a banner.
  useEffect(() => {
    if (!posted) return;
    const t = setTimeout(() => setPosted(false), 4000);
    return () => clearTimeout(t);
  }, [posted]);

  // ── the acts ─────────────────────────────────────────────────────────────
  const post = async (thenClose = false) => {
    if (!share || posting) return;
    setPosting(true);
    setErr(null);
    try {
      const res = await fetch("/api/share", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: share.token, title: postTitle, ground: ground ?? null }),
      });
      const json = (await res.json()) as { ok?: boolean; share?: ShareRow; error?: string };
      if (!json.ok || !json.share) {
        setErr(json.error ?? "that didn't land. try again.");
        return;
      }
      setShare(json.share);
      setTitle(json.share.title ?? target.name);
      setCommitted({ title: json.share.title ?? "", ground: json.share.ground ?? null });
      setPosted(true);
      setWasPosted(true);
      setConfirmClose(false);
      // POSTING HANDS IT OVER, so the link goes with you: the one thing you
      // were always going to do next happens without a second press, and the
      // control says so in place.
      void copy();
      onChanged?.(json.share);
      if (thenClose) onClose();
    } catch {
      setErr("that didn't land. try again.");
    } finally {
      setPosting(false);
    }
  };

  const setVisibility = async (visibility: "private" | "link") => {
    if (!share || share.visibility === visibility) return;
    const before = share;
    setShare({ ...share, visibility });
    try {
      const res = await fetch("/api/share", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: share.token, visibility }),
      });
      const json = (await res.json()) as { ok?: boolean; share?: ShareRow };
      if (!json.ok) {
        setShare(before);
        return setErr("couldn't change who it opens for.");
      }
      onChanged?.(json.share ?? { ...before, visibility });
    } catch {
      setShare(before);
      setErr("couldn't change who it opens for.");
    }
  };

  const revoke = async () => {
    if (!share) return;
    await fetch("/api/share", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: share.token }),
    });
    setShare(null);
    setUrl(null);
    setGone(true);
    // nothing is left to post: the draft and the committed face agree again,
    // so `close` closes without asking about changes that no longer exist.
    setTitle(target.name);
    setGround(null);
    setCommitted({ title: "", ground: null });
    setConfirmClose(false);
    setPosted(false);
    onChanged?.(null);
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return; // the field is selectable — that's the fallback
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const downloadQr = () => {
    const svg = qrWrap.current?.querySelector("svg");
    if (svg) nettiQrDownload(svg as SVGSVGElement, `${faceTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-code.svg`);
  };

  const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const body = (
    <>
      {/* what is behind the cover, receding */}
      <div className="ss-behind" aria-hidden />
      <div className="ss-veil" ref={veilRef} data-gesture-root="" role="dialog" aria-modal aria-label={`share ${target.name}`}>
      <div className="ss-stage">
        {/* ── the face: the page itself, live ────────────────────────────
            The FIELD around the card is the studio's own surface — the ground
            the link opens in, pulled back and frosted — so however dark or
            saturated the pick gets, the page's edge stays findable. */}
        <div className="ss-face">
          <div className="ss-pane" ref={paneRef}>
            {previewPage && frameArmed ? (
              <div className="ss-card" data-ready>
                <div
                  className="ss-frame"
                  ref={plateRef}
                  style={{ width: frameW, height: frameFit.h / frameFit.scale, transform: `scale(${frameFit.scale})` }}
                >
                  {/* the real reading surface, rendered here rather than framed:
                      it needs no token, so it is the page BEFORE it is live, and
                      it arrives on the first frame. It owns nothing global in
                      preview mode (ShareView › `preview`), and the plate carries
                      the ground so the dial paints it as you drag. */}
                  <div className="ss-plate-doc ink-scope" aria-hidden>
                    <ShareView
                      preview
                      state="ok"
                      share={previewPage}
                      signedIn
                      token="preview"
                      ownerGround={null}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <p className="ss-pane-wait">{gone ? "nothing opens here now." : "nothing to show yet."}</p>
            )}
          </div>
          <p className="ss-face-note">
            {gone
              ? "nothing opens here now."
              : !live
                ? "this is the page itself, exactly as anyone opening the link meets it."
                : dirty
                  ? "this is the page itself. it isn't live until you post it."
                  : posted
                    ? "live. anyone opening the link sees exactly this."
                    : `how it opens for whoever you hand it to · ${audience}`}
          </p>
        </div>

        {/* ── the desk ──────────────────────────────────────────────────── */}
        <div className="ss-desk">
          <header className="ss-desk-head">
            {/* the same round × the share card wears, top right of the desk; it
                asks about unposted changes first, like Escape */}
            <button type="button" className="icon-circle ss-close" onClick={tryClose} aria-label="close">
              <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
                <path d="M5.5 5.5 14.5 14.5M14.5 5.5 5.5 14.5" />
              </svg>
            </button>
            <h1 className="ss-h">share {target.name}</h1>
            <p className="ss-sub">
              {live
                ? "a read-only door into this memory. you decide what it looks like and who it opens for."
                : "the page you are on, as anyone you hand it to will meet it."}
            </p>
          </header>

          {err && <p className="ss-warn">{err}</p>}

          {gone ? (
            <p className="ss-warn">
              the link is gone. anyone holding it now finds nothing. close this and share again to open a fresh door.
            </p>
          ) : (
            <>
              {/* what it's called — the field arrives holding the page's own
                  name, so this is an edit, not a blank to fill. */}
              {live && (
                <Field
                  label="what it's called"
                  type="text"
                  value={title}
                  placeholder={target.name}
                  maxLength={160}
                  disabled={busy || !share}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void post();
                  }}
                />
              )}

              {/* who the door opens for — not a draft. A permission lands the
                  moment it is pressed, and the copy says exactly that.
                  TWO MARKS SIDE BY SIDE: open on the left (the default, and the
                  reason anyone is here), closed on the right. Both wear the cell
                  (law_cell) — the globe striped like the world, the padlock the
                  security surface already draws. */}
              {live ? (
                <div className="ss-levels">
                  <span className="ss-levels-label">who it opens for · takes effect at once</span>
                  {isRoom ? (
                    <p className="ss-room-note">
                      this door was opened into {target.spaceName ?? "a room"} — everyone standing in the
                      room can read it. change that by unlinking it from the room.
                    </p>
                  ) : (
                    <>
                      <div className="ss-levels-row">
                        <button
                          className="sp-level"
                          data-on={openToAll}
                          disabled={busy || !share}
                          onClick={() => void setVisibility("link")}
                        >
                          <span className="sp-level-mark" aria-hidden><GlobeCell /></span>
                          <span className="sp-level-name">anyone with the link</span>
                          <span className="sp-level-sub">people and AIs holding it can read this</span>
                        </button>
                        <button
                          className="sp-level"
                          data-on={!openToAll}
                          disabled={busy || !share}
                          onClick={() => void setVisibility("private")}
                        >
                          <span className="sp-level-mark" aria-hidden><PadlockCell size={20} /></span>
                          <span className="sp-level-name">only you</span>
                          <span className="sp-level-sub">the door stays closed to everyone else</span>
                        </button>
                      </div>
                      {isSkill && (
                        <p className="ss-room-note" data-on={openToAll || undefined}>
                          {openToAll
                            ? "this is a skill, and it's open, so it also appears on the "
                            : "this is a skill. open it to anyone with the link and it also appears on the "}
                          <a className="quiet-link" href="/community" target="_blank" rel="noopener noreferrer">community skills page</a>
                          , wearing your name — always the version you keep now, never a copy.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <p className="ss-public-say">
                  this page is public. the link is its interface: hand it to a person or an ai and
                  it opens exactly this.
                </p>
              )}

              {/* the link itself — and, beside it, the one act that ends it.
                  `let this link go` belongs next to the link it destroys, not
                  at the far end of the desk. */}
              <div className="sp-hero" data-state={busy ? "minting" : "live"}>
                <div className="sp-hero-top">
                  <span className="sp-hero-label">
                    {busy
                      ? "opening a door…"
                      : !live
                        ? "this page is its own link"
                        : isRoom
                          ? `this link opens for everyone in ${target.spaceName ?? "the room"}`
                          : openToAll
                            ? "this link is live"
                            : "this link opens for you only"}
                  </span>
                  {live && share && !gone && (
                    <button className="quiet-link ss-let" onClick={() => void revoke()}>
                      let this link go
                    </button>
                  )}
                </div>
                {/* the link, and the taking of it, in ONE object. A copy pill
                    beside the field made the act look like a second decision;
                    the link is there to be taken, so the control sits in it. */}
                <div className="sp-hero-link">
                  <input
                    className="input-soft sp-hero-url"
                    readOnly
                    value={url ?? ""}
                    placeholder="minting…"
                    aria-label="share link"
                    onFocus={(e) => e.target.select()}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    type="button"
                    className="sp-hero-copy"
                    data-done={copied || undefined}
                    disabled={!url}
                    onClick={() => void copy()}
                    aria-label={copied ? "copied" : "copy the link"}
                  >
                    <CopyBadge done={copied} />
                  </button>
                </div>
                {canNative && url && (
                  <button
                    className="quiet-link sp-send"
                    onClick={() => void navigator.share({ title: faceTitle, url }).catch(() => {})}
                  >
                    send it…
                  </button>
                )}
              </div>

              {url && (
                <div className="sp-qr" ref={qrWrap}>
                  {/* smaller, and simpler so it stays crisp at that size: one
                      correction level down (Q still carries the cell at its
                      heart), fatter modules, the ground as its paper. */}
                  <NettiQr
                    data={url}
                    size={128}
                    ecc="Q"
                    paper={qrPaper}
                    ink={qrInk}
                    core={inks.core}
                    soft={inks.soft}
                    heart="#ffffff"
                  />
                  <div className="sp-qr-side">
                    <p className="sp-qr-note">
                      your cell, as a code. a camera opens it; an AI reading a screenshot of it can
                      follow it to this memory, if the door is open to them.
                    </p>
                    <button className="quiet-link" onClick={downloadQr}>
                      download the code
                    </button>
                    {typeof share?.view_count === "number" && share.view_count > 0 && (
                      <span className="sp-qr-views">
                        opened {share.view_count} {share.view_count === 1 ? "time" : "times"}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* the fixed slot — the blast radius in one line above it, and a
              reaching act asks twice in the same place (law_open_dont_unfold). */}
          <div className="ss-foot">
            {confirmClose ? (
              <>
                <span className="ss-foot-say">the title and the ground aren&rsquo;t on the link yet.</span>
                <button className="quiet-link" style={{ color: "var(--ink-3)" }} onClick={onClose}>
                  leave them
                </button>
                <PillButton loading={posting} onClick={() => void post(true)}>
                  post &amp; close
                </PillButton>
              </>
            ) : (
              <>
                {/* WHAT THE LINK IS RIGHT NOW. A door that is already open says
                    so, quietly and continuously — you should never have to
                    remember whether you posted it. */}
                <span className="ss-foot-say" aria-live="polite">
                  {dirty
                    ? posted || committed.title || committed.ground
                      ? "not on the link yet"
                      : "not live yet"
                    : live && share
                      ? "live"
                      : ""}
                </span>
                {live && !gone && (
                  <PillButton loading={posting} disabled={!dirty || !share} onClick={() => void post()}>
                    {/* the words say what pressing it DOES: a door that is
                        already open is being updated, not posted again. */}
                    {wasPosted ? "update the link" : "post it live"}
                  </PillButton>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── the ONE picker, where it always is: the bottom-left corner ────
          Same orb, same arcs, same corner as the dashboard's ground dial — so
          changing how this page opens is the same gesture as changing how
          YOUR innernet opens, in the same place on the screen. It paints the
          live page in the frame as you drag. */}
      {live && !gone && (
        <div className="ss-dial" data-noswipe style={{ ["--ss-orb" as string]: `${Math.round(dialOrb)}px` }}>
          <ArcColorPicker
            collapsible
            layout="bl"
            scale={dialScale}
            orbScale={dialOrbScale}
            value={plateGround}
            onChange={(hex) => setGround(hex)}
            label="the ground this link opens in"
          />
          <span className="ss-dial-say">
            {ground ? (
              <>
                fixed for this link ·{" "}
                <button className="quiet-link" onClick={() => setGround(null)}>follow mine again</button>
              </>
            ) : (
              "follows your ground, live"
            )}
          </span>
        </div>
      )}
      </div>
    </>
  );

  return typeof document !== "undefined" ? createPortal(body, document.body) : null;
}
